import { tw } from 'utils/classnames'
import { TagBase } from './tag-base'

import type { TagBaseProps } from './tag-base'

export type TagLabelCategoryColor =
  | 'gray'
  | 'magenta'
  | 'yellow'
  | 'pink'
  | 'orange'
  | 'blue'
  | 'overlay'

export type TagLabelVisualWeight = 'strong' | 'regular'

export interface TagLabelProps extends TagBaseProps {
  categoryColor?: TagLabelCategoryColor
  visualWeight?: TagLabelVisualWeight
}

/* eslint-disable @stylistic/max-len */
const labelClasses: Record<string, string> = {
  gray_regular:
    'bg-theme_semantic_informational_attribute_gray_background-color-regular ring-theme_semantic_informational_attribute_gray_border-color-regular text-theme_semantic_informational_attribute_gray_foreground-color-regular',
  gray_strong:
    'bg-theme_semantic_informational_attribute_gray_background-color-strong ring-theme_semantic_informational_attribute_gray_border-color-strong text-theme_semantic_informational_attribute_gray_foreground-color-strong',

  magenta_regular:
    'bg-theme_semantic_informational_attribute_magenta_background-color-regular ring-theme_semantic_informational_attribute_magenta_border-color-regular text-theme_semantic_informational_attribute_magenta_foreground-color-regular',
  magenta_strong:
    'bg-theme_semantic_informational_attribute_magenta_background-color-strong ring-theme_semantic_informational_attribute_magenta_border-color-strong text-theme_semantic_informational_attribute_magenta_foreground-color-strong',

  yellow_regular:
    'bg-theme_semantic_informational_attribute_yellow_background-color-regular ring-theme_semantic_informational_attribute_yellow_border-color-regular text-theme_semantic_informational_attribute_yellow_foreground-color-regular',
  yellow_strong:
    'bg-theme_semantic_informational_attribute_yellow_background-color-strong ring-theme_semantic_informational_attribute_yellow_border-color-strong text-theme_semantic_informational_attribute_yellow_foreground-color-strong',

  pink_regular:
    'bg-theme_semantic_informational_attribute_pink_background-color-regular ring-theme_semantic_informational_attribute_pink_border-color-regular text-theme_semantic_informational_attribute_pink_foreground-color-regular',
  pink_strong:
    'bg-theme_semantic_informational_attribute_pink_background-color-strong ring-theme_semantic_informational_attribute_pink_border-color-strong text-theme_semantic_informational_attribute_pink_foreground-color-strong',

  orange_regular:
    'bg-theme_semantic_informational_attribute_orange_background-color-regular ring-theme_semantic_informational_attribute_orange_border-color-regular text-theme_semantic_informational_attribute_orange_foreground-color-regular',
  orange_strong:
    'bg-theme_semantic_informational_attribute_orange_background-color-strong ring-theme_semantic_informational_attribute_orange_border-color-strong text-theme_semantic_informational_attribute_orange_foreground-color-strong',

  blue_regular:
    'bg-theme_semantic_informational_attribute_blue_background-color-regular ring-theme_semantic_informational_attribute_blue_border-color-regular text-theme_semantic_informational_attribute_blue_foreground-color-regular',
  blue_strong:
    'bg-theme_semantic_informational_attribute_blue_background-color-strong ring-theme_semantic_informational_attribute_blue_border-color-strong text-theme_semantic_informational_attribute_blue_foreground-color-strong',

  overlay_regular:
    'bg-theme_semantic_informational_attribute_overlay_background-color-regular ring-theme_semantic_informational_attribute_overlay_border-color-regular text-theme_semantic_informational_attribute_overlay_foreground-color-regular backdrop-blur-[8px]',
  overlay_strong:
    'bg-theme_semantic_informational_attribute_overlay_background-color-strong ring-theme_semantic_informational_attribute_overlay_border-color-strong text-theme_semantic_informational_attribute_overlay_foreground-color-strong backdrop-blur-[8px]',
}
/* eslint-enable @stylistic/max-len */

/**
 * TagLabel -- a non-interactive display tag for categorical/informational labels.
 *
 * @example
 * ```tsx
 * <TagLabel categoryColor="magenta" visualWeight="strong">
 *   Crafts
 * </TagLabel>
 * ```
 *
 * @figma https://www.figma.com/design/wx3OX1hccLxomMLDn6QXDa/Studio-DS---Playground?node-id=2346-3054
 */
export function TagLabel({
  categoryColor = 'gray',
  visualWeight = 'strong',
  className,
  ...props
}: TagLabelProps) {
  const classes = labelClasses[`${categoryColor}_${visualWeight}`] ?? labelClasses.gray_strong

  return <TagBase className={tw(classes, className)} {...props} />
}
